import s from "../styles/modules/SignUpSlot.module.scss";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import DeleteDialog from "./DeleteDialog";
import SameDayCancelDialog from "./SameDayCancelDialog";
import { cancelBookingIsAvailable, isSameDayCancellationAvailable } from "@/utils/booking";
import { formatCancellationDeadline, formatDateWithTime } from "@/utils/dateFormatters";
import {
  Clock,
  User,
  Building2,
  UserCircle,
  CalendarDays,
  Trash2,
  MapPin,
  AlertTriangle,
} from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { useSignUpSlot } from "../hooks/useSignUpSlot";
import { useClub } from "@/contexts/ClubContext";
import { SignUpSlotProps } from "../@types/props.types";

const SignUpSlot = ({
  trainers,
  slot,
  appointmentsData,
  userName,
  onSuccessfulSignUp,
}: SignUpSlotProps) => {
  const { selectedClubName } = useClub();
  const isBookingInDifferentClub = slot.club_title && slot.club_title !== selectedClubName;
  const {
    isTimeBooked,
    open,
    deleteDialog,
    sameDayCancelDialog,
    handleSignUp,
    handleCancelClass,
    handleConfirmDelete,
    handleSameDayCancel,
    handleConfirmSameDayCancel,
    handleDeleteSameDayCancel,
    handleConfirmDeleteSameDayCancel,
    deleteSameDayCancelDialog,
    setDeleteSameDayCancelDialog,
    hasSameDayCancellation,
    trainerListActiveState,
    bookedTrainerName,
    setOpen,
    setDeleteDialog,
    setSameDayCancelDialog,
    isBookingAvailable,
  } = useSignUpSlot({
    slot,
    trainers,
    appointmentsData,
    userName,
    onSuccessfulSignUp,
  });

  return (
    <div
      className={`${s.wrapper} ${
        isTimeBooked ? "border-primary/20 bg-muted/50 pb-2" : "bg-muted/50"
      }`}
    >
      <div className={s.content}>
        <div className={s.timeBlock}>
          <Clock className={s.icon} />
          <span>{slot.date_time.slice(-5)}</span>
        </div>
        <div className={s.trainerBlock}>
          <User className={s.icon} />
          <div className="flex flex-row gap-2">
            {trainerListActiveState.length > 0
              ? trainerListActiveState.map((trainer, index) => (
                  <div key={index} className="whitespace-nowrap text-sm font-normal">
                    {trainer.name}
                  </div>
                ))
              : bookedTrainerName && (
                  <div className="whitespace-nowrap text-sm font-normal">{bookedTrainerName}</div>
                )}
          </div>
        </div>
      </div>
      {!isTimeBooked && isBookingAvailable && (
        <Button variant="default" size="sm" onClick={() => setOpen(true)} className={s.button}>
          Записаться
        </Button>
      )}
      {isTimeBooked && (
        <div className="mt-auto w-full">
          <div className="flex items-center justify-start pt-2">
            <UserCircle className={s.icon} />
            <div className="ml-2 text-sm">{userName}</div>
            <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              Вы записаны
            </span>
          </div>
          {isBookingInDifferentClub && (
            <div className="mt-2 flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-amber-700">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm font-medium">Филиал: {slot.club_title}</span>
            </div>
          )}
          {cancelBookingIsAvailable(slot.date_time) ? (
            <Button
              variant="outline"
              size="sm"
              className="mt-4 h-10 w-full border-destructive/30 text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={handleCancelClass}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Отменить занятие
            </Button>
          ) : hasSameDayCancellation === true ? (
            <div className="mt-4 flex w-full flex-col items-center gap-1">
              <div className="flex h-10 w-full items-center justify-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 text-sm text-amber-600">
                <Clock className="h-4 w-4" />
                Запрос на отмену отправлен
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground hover:text-destructive"
                onClick={handleDeleteSameDayCancel}
              >
                <Trash2 className="mr-1 h-3 w-3" />
                Отменить запрос
              </Button>
            </div>
          ) : hasSameDayCancellation === false && isSameDayCancellationAvailable(slot.date_time) ? (
            <Button
              variant="outline"
              size="sm"
              className="mt-4 h-10 w-full border-amber-300 text-amber-600 hover:bg-amber-600 hover:text-white"
              onClick={handleSameDayCancel}
            >
              <AlertTriangle className="mr-2 h-4 w-4" />
              Отмена день в день до 10:00
            </Button>
          ) : hasSameDayCancellation !== null ? (
            <Button variant="outline" disabled className="mt-4 w-full">
              Отмена этого занятия недоступна после 18:00 -{" "}
              {formatCancellationDeadline(slot.date_time)}
            </Button>
          ) : null}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="overflow-hidden rounded-lg p-0 sm:max-w-[425px]">
          <div className="bg-primary/10 p-6">
            <DialogHeader className="space-y-3">
              <DialogTitle className="text-center text-xl font-semibold">
                Запись на занятие
              </DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-4 pt-3">
                  <Card>
                    <CardContent className="space-y-3 p-4">
                      <div className="flex items-center gap-3 text-gray-600">
                        <Building2 className="h-5 w-5 text-primary" />
                        <span>АкваСтар {selectedClubName}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <CalendarDays className="h-5 w-5 text-primary" />
                        <span>{formatDateWithTime(slot.date_time)}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <User className="h-5 w-5 text-primary" />
                        <span>Инструктор: {trainerListActiveState[0]?.name || "Не указан"}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <UserCircle className="h-5 w-5 text-primary" />
                        <span>Ребенок: {userName}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="border-t bg-white p-6">
            <DialogFooter className="gap-3 sm:justify-end">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Отмена
              </Button>
              <Button onClick={handleSignUp} size="lg">
                Записаться
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteDialog
        isOpen={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
      />

      <SameDayCancelDialog
        isOpen={sameDayCancelDialog}
        onClose={() => setSameDayCancelDialog(false)}
        onConfirm={handleConfirmSameDayCancel}
      />

      <DeleteDialog
        isOpen={deleteSameDayCancelDialog}
        onClose={() => setDeleteSameDayCancelDialog(false)}
        onConfirm={handleConfirmDeleteSameDayCancel}
      />
    </div>
  );
};

export const SignUpSlotStrSkeleton = () => {
  return (
    <div className={s.wrapper}>
      <div className={s.content}>
        <div className={s.timeBlock}>
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className={s.trainerBlock}>
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <Skeleton className="h-8 w-[90px]" />
    </div>
  );
};

export default SignUpSlot;
